import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, UserCircle2, X, Save, DollarSign, TrendingUp, Clock, CalendarClock, Users } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { api } from '@/lib/api';
import { ADMIN } from '@/constants/testIds';
import SessionDialog from '@/components/admin/SessionDialog';
import ConfirmDialog from '@/components/admin/ConfirmDialog';
import AdminSearch from '@/components/admin/AdminSearch';
import { logger } from '@/lib/logger';

const LEVELS = ['Beginner', 'Intermediate', 'Advanced', 'Pro'];

const emptyClient = { name: '', email: '', phone: '', level: 'Intermediate', hourly_rate: 0, goals: '', notes: '', tags: [] };

export default function AdminClients() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [dialog, setDialog] = useState({ open: false, mode: 'create', data: emptyClient, id: null });
  const [detail, setDetail] = useState({ open: false, clientId: null, summary: null });
  const [sessionDialog, setSessionDialog] = useState({ open: false, existing: null, defaultStart: null });
  const [confirmDelete, setConfirmDelete] = useState({ open: false, id: null, name: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get('/admin/clients'); setItems(data || []); }
    catch (err) { logger.warn('Failed to load clients', err); toast.error('Failed to load clients'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return items;
    return items.filter((c) =>
      c.name?.toLowerCase().includes(term) ||
      c.email?.toLowerCase().includes(term) ||
      c.phone?.toLowerCase().includes(term) ||
      c.level?.toLowerCase().includes(term)
    );
  }, [items, q]);

  const openCreate = () => setDialog({ open: true, mode: 'create', data: emptyClient, id: null });
  const openEdit = (c) => setDialog({ open: true, mode: 'edit', data: { ...c }, id: c.id });
  const close = () => setDialog({ open: false, mode: 'create', data: emptyClient, id: null });

  const save = async (e) => {
    e?.preventDefault();
    const d = dialog.data;
    if (!d.name.trim()) { toast.error('Name is required.'); return; }
    const payload = { ...d, hourly_rate: parseFloat(d.hourly_rate) || 0 };
    try {
      if (dialog.mode === 'create') { await api.post('/admin/clients', payload); toast.success('Client created.'); }
      else { await api.put(`/admin/clients/${dialog.id}`, payload); toast.success('Client updated.'); }
      close(); load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to save'); }
  };

  const del = async (id, name) => {
    setConfirmDelete({ open: true, id, name });
  };
  const performDelete = async () => {
    const { id } = confirmDelete;
    try { await api.delete(`/admin/clients/${id}`); toast.success('Client deleted.'); load(); }
    catch { toast.error('Failed to delete'); }
    finally { setConfirmDelete({ open: false, id: null, name: '' }); }
  };

  const openDetail = async (client) => {
    setDetail({ open: true, clientId: client.id, summary: null });
    try {
      const { data } = await api.get(`/admin/clients/${client.id}/summary`);
      setDetail({ open: true, clientId: client.id, summary: data });
    } catch { toast.error('Failed to load client details'); }
  };

  const refreshDetail = async () => {
    if (!detail.clientId) return;
    try {
      const { data } = await api.get(`/admin/clients/${detail.clientId}/summary`);
      setDetail((d) => ({ ...d, summary: data }));
    } catch (err) { logger.warn("Silent failure:", err); }
  };

  return (
    <div>
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl leading-tight tracking-tight inline-flex items-center gap-2"><Users className="w-7 h-7 text-[var(--pg-admin-accent)]" /> Clients</h1>
          <p className="mt-1 text-sm text-white/60">Your coaching roster — session history, revenue, and notes.</p>
        </div>
        <button data-testid={ADMIN.createClientBtn} onClick={openCreate} className="inline-flex items-center gap-2 bg-[#F2B705] hover:bg-[#FFC83D] text-[#141414] font-semibold px-4 py-2.5 rounded-xl transition-colors">
          <Plus className="w-4 h-4" /> New client
        </button>
      </div>

      <div className="mt-4">
        <AdminSearch value={q} onChange={setQ} placeholder="Search by name, email, phone, or level..." />
      </div>

      <div data-testid={ADMIN.clientsTable} className="mt-6 rounded-2xl border border-white/10 bg-[#141414] overflow-hidden">
        {loading ? <div className="p-6 text-sm text-white/60">Loading...</div> :
          filtered.length === 0 ? (
            <div className="p-10 text-center text-white/60">
              <UserCircle2 className="w-8 h-8 mx-auto mb-3 text-white/30" />
              No clients yet. Create your first one.
            </div>
          ) : (
            <ul className="divide-y divide-white/5">
              {filtered.map((c) => (
                <li key={c.id} data-testid={ADMIN.clientRow(c.id)} className="flex items-center gap-3 px-4 py-3 hover:bg-white/[0.02]">
                  <button onClick={() => openDetail(c)} className="flex items-center gap-3 flex-1 min-w-0 text-left">
                    <span className="inline-flex w-10 h-10 items-center justify-center rounded-full bg-[#F2B705] text-[#141414] font-display shrink-0">{c.name.charAt(0)}</span>
                    <div className="min-w-0">
                      <div className="font-semibold truncate">{c.name}</div>
                      <div className="text-xs text-white/50 truncate">{c.email || '—'}{c.phone ? ` · ${c.phone}` : ''}</div>
                    </div>
                  </button>
                  <span className="hidden sm:inline-flex text-[10px] uppercase tracking-widest border border-white/10 rounded-full px-2 py-0.5 text-white/70">{c.level}</span>
                  <span className="hidden md:inline text-xs text-white/60">{c.hourly_rate ? `SAR ${c.hourly_rate}/hr` : '—'}</span>
                  <button onClick={() => openEdit(c)} className="p-2 rounded-lg hover:bg-white/5 text-white/80" aria-label="Edit"><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => del(c.id, c.name)} className="p-2 rounded-lg hover:bg-red-500/10 text-red-300" aria-label="Delete"><Trash2 className="w-4 h-4" /></button>
                </li>
              ))}
            </ul>
          )}
      </div>

      {/* Create/Edit dialog */}
      {dialog.open && (
        <div className="fixed inset-0 z-40 flex items-end sm:items-center justify-center p-0 sm:p-4">
          <div className="absolute inset-0 bg-black/70" onClick={close} />
          <form onSubmit={save} className="relative w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl border border-white/10 bg-[#141414] p-5 sm:p-6 h-[92dvh] sm:h-auto sm:max-h-[92vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-2xl">{dialog.mode === 'create' ? 'New Client' : 'Edit Client'}</h3>
              <button type="button" onClick={close} className="p-2 rounded-lg hover:bg-white/5"><X className="w-4 h-4" /></button>
            </div>
            <div className="space-y-3">
              <Field label="Name"><input data-testid={ADMIN.clientNameInput} value={dialog.data.name} onChange={(e) => setDialog({ ...dialog, data: { ...dialog.data, name: e.target.value } })} className="pg-inp" /></Field>
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Email"><input data-testid={ADMIN.clientEmailInput} value={dialog.data.email} onChange={(e) => setDialog({ ...dialog, data: { ...dialog.data, email: e.target.value } })} className="pg-inp" /></Field>
                <Field label="Phone"><input data-testid={ADMIN.clientPhoneInput} value={dialog.data.phone} onChange={(e) => setDialog({ ...dialog, data: { ...dialog.data, phone: e.target.value } })} className="pg-inp" /></Field>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Level">
                  <select data-testid={ADMIN.clientLevelSelect} value={dialog.data.level} onChange={(e) => setDialog({ ...dialog, data: { ...dialog.data, level: e.target.value } })} className="pg-inp">
                    {LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
                  </select>
                </Field>
                <Field label="Hourly rate (SAR)">
                  <input data-testid={ADMIN.clientRateInput} type="number" min={0} step="0.01" value={dialog.data.hourly_rate} onChange={(e) => setDialog({ ...dialog, data: { ...dialog.data, hourly_rate: e.target.value } })} className="pg-inp" />
                </Field>
              </div>
              <Field label="Goals">
                <textarea data-testid={ADMIN.clientGoalsInput} rows={2} value={dialog.data.goals} onChange={(e) => setDialog({ ...dialog, data: { ...dialog.data, goals: e.target.value } })} className="pg-inp resize-none" placeholder="What are they working towards?" />
              </Field>
              <Field label="Notes">
                <textarea data-testid={ADMIN.clientNotesInput} rows={3} value={dialog.data.notes} onChange={(e) => setDialog({ ...dialog, data: { ...dialog.data, notes: e.target.value } })} className="pg-inp resize-none" placeholder="Playing style, strengths, injuries, etc." />
              </Field>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button type="button" onClick={close} className="px-4 py-2 rounded-xl border border-white/10 hover:bg-white/5">Cancel</button>
              <button data-testid={ADMIN.clientSaveBtn} type="submit" className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#F2B705] hover:bg-[#FFC83D] text-[#141414] font-semibold"><Save className="w-4 h-4" /> Save</button>
            </div>
            <style>{`.pg-inp{width:100%;border:1px solid rgba(255,255,255,.1);background:rgba(0,0,0,.3);border-radius:.75rem;padding:.6rem .9rem;color:#fff;outline:none;transition:border-color .15s;}.pg-inp:focus{border-color:#F2B705;box-shadow:0 0 0 3px rgba(242,183,5,.25);}`}</style>
          </form>
        </div>
      )}

      {/* Detail drawer */}
      {detail.open && (
        <div className="fixed inset-0 z-40 flex items-stretch justify-end">
          <div className="absolute inset-0 bg-black/70" onClick={() => setDetail({ open: false, clientId: null, summary: null })} />
          <div className="relative w-full sm:max-w-2xl h-full bg-[#141414] border-l border-white/10 overflow-y-auto">
            {!detail.summary ? (
              <div className="p-8 text-white/60 text-sm">Loading client...</div>
            ) : (
              <ClientDetail
                summary={detail.summary}
                onClose={() => setDetail({ open: false, clientId: null, summary: null })}
                onEditSession={(s) => setSessionDialog({ open: true, existing: s, defaultStart: null })}
                onNewSession={() => setSessionDialog({ open: true, existing: null, defaultStart: new Date() })}
              />
            )}
          </div>
        </div>
      )}

      <SessionDialog
        open={sessionDialog.open}
        onClose={() => setSessionDialog({ open: false, existing: null, defaultStart: null })}
        onSaved={() => { refreshDetail(); }}
        existing={sessionDialog.existing || (detail.summary?.client && !sessionDialog.existing ? { client_ids: [detail.summary.client.id], client_names: [detail.summary.client.name] } : null)}
        clients={items}
        defaultStart={sessionDialog.defaultStart}
      />

      <ConfirmDialog
        open={confirmDelete.open}
        onOpenChange={(v) => setConfirmDelete((c) => ({ ...c, open: v }))}
        title={`Delete ${confirmDelete.name || 'this client'}?`}
        description="Their session history will remain (detached), but their profile will be removed. This cannot be undone."
        confirmText="Delete client"
        destructive
        onConfirm={performDelete}
        confirmTestId="admin-clients-confirm-delete"
        cancelTestId="admin-clients-cancel-delete"
      />
    </div>
  );
}

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-xs uppercase tracking-widest text-white/60 font-semibold">{label}</span>
    <div className="mt-1">{children}</div>
  </label>
);

const money = (v) => `SAR ${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const ClientDetail = ({ summary, onClose, onEditSession, onNewSession }) => {
  const c = summary.client;
  return (
    <div>
      <div className="p-5 sm:p-6 border-b border-white/5 flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <span className="inline-flex w-12 h-12 items-center justify-center rounded-full bg-[#F2B705] text-[#141414] font-display text-xl shrink-0">{c.name.charAt(0)}</span>
          <div className="min-w-0">
            <div className="font-display text-2xl leading-tight truncate">{c.name}</div>
            <div className="text-xs text-white/60 truncate">{c.email || '—'}{c.phone ? ` · ${c.phone}` : ''}</div>
            <div className="mt-1 text-[10px] uppercase tracking-widest text-[#F2B705]">{c.level}{c.hourly_rate ? ` · ${money(c.hourly_rate)}/hr` : ''}</div>
          </div>
        </div>
        <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/5" aria-label="Close"><X className="w-4 h-4" /></button>
      </div>

      <div className="p-5 sm:p-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat icon={CalendarClock} label="Sessions" value={summary.total_sessions} />
        <Stat icon={Clock} label="Upcoming" value={summary.upcoming_sessions} />
        <Stat icon={TrendingUp} label="Revenue" value={money(summary.revenue_total)} />
        <Stat icon={DollarSign} label="Outstanding" value={money(summary.revenue_outstanding)} accent="text-red-300" />
      </div>

      {(c.goals || c.notes) && (
        <div className="px-5 sm:px-6 pb-2 space-y-3">
          {c.goals && <InfoRow label="Goals" value={c.goals} />}
          {c.notes && <InfoRow label="Notes" value={c.notes} />}
        </div>
      )}

      <div className="px-5 sm:px-6 py-4 flex items-center justify-between border-b border-white/5">
        <h4 className="font-semibold">Session history</h4>
        <button onClick={onNewSession} className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg bg-[#F2B705] hover:bg-[#FFC83D] text-[#141414] font-semibold">
          <Plus className="w-3.5 h-3.5" /> Book session
        </button>
      </div>

      {summary.sessions.length === 0 ? (
        <div className="p-6 text-sm text-white/60 text-center">No sessions yet.</div>
      ) : (
        <ul className="divide-y divide-white/5">
          {summary.sessions.map((s) => (
            <li key={s.id}>
              <button onClick={() => onEditSession(s)} className="w-full text-left px-5 sm:px-6 py-3 hover:bg-white/[0.03] flex items-center gap-3">
                <div className="font-display text-lg text-[var(--pg-admin-accent)] w-20 shrink-0">{format(parseISO(s.start_time), 'MMM d')}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold truncate">{s.session_name || s.session_type}</span>
                    <span className="text-xs text-white/50">· {format(parseISO(s.start_time), 'HH:mm')} · {s.duration_min}m</span>
                  </div>
                  <div className="text-xs text-white/50 mt-0.5 truncate">
                    {s.status}
                    {(s.client_ids || []).length > 1 && ` · ${(s.client_ids || []).length} clients`}
                    {s.location ? ` · ${s.location}` : ''}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-semibold">{money(s.price_per_client ?? s.total_price ?? s.price)}</div>
                  <div className="text-[10px] uppercase tracking-widest text-white/40">{(s.client_ids || []).length > 1 ? 'this client' : 'total'}</div>
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const Stat = ({ icon: Icon, label, value, accent }) => (
  <div className="rounded-xl border border-white/10 p-3">
    <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-white/50"><Icon className="w-3 h-3" /> {label}</div>
    <div className={`mt-1 font-display text-xl leading-none ${accent || 'text-white'}`}>{value}</div>
  </div>
);

const InfoRow = ({ label, value }) => (
  <div className="rounded-xl border border-white/10 bg-black/20 p-3">
    <div className="text-[10px] uppercase tracking-widest text-white/50">{label}</div>
    <div className="mt-1 text-sm text-white/85 whitespace-pre-wrap">{value}</div>
  </div>
);
